import {useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import './App.css';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import logo from '../src/images/IFPRSLogoGreen.png';
import CircularProgress from '@mui/material/CircularProgress';
import InFormAlert from './components/InFormAlert';
import packageInfo from '../package.json';
import {useSelector} from "react-redux";
import type {RootState} from "./redux/store.tsx";

const BlueAlien = "#1976d2";

const theme = createTheme({
  overrides: {
    MuiDialog: {
      root: {
        top: '94px !important'
      },
    },
  },
});

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

const Login = ({userCredential, isUserFound, logout, isInReadWriteGroup, isCheckingCredentials}) => {
  const { height } = useWindowDimensions();
  const userRoleInfo = useSelector((state: RootState) => state.globalContext.userRoleInfo);
  const [browserWarningOpen, setBrowserWarningOpen] = useState(false);
  const [browserWarningAcknowledged, setBrowserWarningAcknowledged] = useState(false);

  useEffect(() => {
    if (!browserWarningAcknowledged && !(window.navigator.userAgent.indexOf('Chrome') !== -1 
    || (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)))
    && !(window.navigator.userAgent.indexOf('Edge') !== -1 || 
    (!(/*@cc_on!@*/false || !!document.documentMode) && !!window.StyleMedia))){
      setBrowserWarningOpen(true);
    } 
  });

  const handleBrowserAlertClose = () => {
    setBrowserWarningOpen(false);
    setBrowserWarningAcknowledged(true);
  }

  const attemptLogin = (e) =>
  {
    e.preventDefault();
    window.authManager.login();
  }

  const splashStyle = {
    backgroundSize: 'cover, cover',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  }

  const hasNoRolesBlock = (
    <div className="login-container" style={splashStyle}>
          <ThemeProvider theme={theme}>
            <Dialog open={true}>
            <div style={{ margin: 20, color: 'rgba(73, 73, 73, 0.6)'}}>
              <div style={{textAlign: 'center'}}>
                <img src={logo} width="240px" alt="IFPRS" style={{paddingBottom: 5}} />
                <p>Your account doesn't appear to have any User Roles assigned to it for InFORM.</p>
                <p>Please provide your username to your Bureau or Office InFORM–Fuels Administrator</p>
                <p>Or you can sign out and log back in with a different NIFC Org account.</p>
                <div>
                  <Button
                    variant="contained"
                    style={{backgroundColor: BlueAlien}}
                    fullWidth
                    onClick={logout}
                  >Sign Out</Button>
                </div>
              </div>
            </div>
            </Dialog>
          </ThemeProvider>
        </div>
  )

  const hasNoReadWritePermissionsBlock = (
    <div className="login-container" style={splashStyle}>
          <ThemeProvider theme={theme}>
            <Dialog open={true}>
              <div style={{ margin: 20, color: 'rgba(73, 73, 73, 0.6)'}}>
                <div style={{textAlign: 'center'}}>
                  <img src={logo} width="240px" alt="IFPRS" style={{paddingBottom: 5}} />
                  <p>Your InFORM Fuels account doesn't appear to have Read/Write privileges assigned to it for InFORM.</p>
                  <p>Please provide your username to your Bureau or Office InFORM–Fuels Administrator</p>
                  <p>Or you can sign out and log back in with a different NIFC Org account.</p>
                  <div>
                    <Button
                      variant="contained"
                      style={{backgroundColor: BlueAlien}}
                      fullWidth
                      onClick={logout}
                    >Sign Out</Button>
                  </div>
                </div>
              </div>
            </Dialog>
          </ThemeProvider>
        </div>
  )

  const genericErrorBlock = (
    //Should never get here
    <div className="login-container" style={splashStyle}>
          <ThemeProvider theme={theme}>
            <Dialog open={true}>
              <div style={{ margin: 20, color: 'rgba(73, 73, 73, 0.6)'}}>
                <div style={{textAlign: 'center'}}>
                <img src={logo} width="240px" alt="IFPRS" style={{paddingBottom: 5}} />
                  <p>Error:</p>
                  <p>Inform Fuels has encountered an error and cannot proceed.</p>
                  <p>You may try reloading your browser.</p>
                </div>
              </div>
            </Dialog>
          </ThemeProvider>
        </div>
  )

  const spinnerBlock = (
    <div className="login-container" style={splashStyle}>
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: 'transparent', height: height, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignContent: 'center', flexWrap: 'wrap'}}>
          <div style={{ margin: 20 }}>
            <div style={{textAlign: 'center'}}>
            <CircularProgress size={200} style={{ color: BlueAlien }} />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </div>
  )

  const errorBlock = (userCredential && userRoleInfo && userRoleInfo.length === 0) ? (hasNoRolesBlock) : (userCredential && !isInReadWriteGroup) ? (hasNoReadWritePermissionsBlock) : (genericErrorBlock)

  const loginBlock = (
    <div className="login-container" style={splashStyle}>
      <InFormAlert 
          openFlag={browserWarningOpen} 
          closeMethod={handleBrowserAlertClose}
          buttonMethod={handleBrowserAlertClose}
          titleText={"Unsupported Browser"}
          contentText={"InFORM supports Edge and Chrome. Please use one of these two browsers."}
        />
      <ThemeProvider theme={theme}>
      <Dialog open={true} slotProps={{paper: {style: {minWidth: 825}}}}>
        <div style={{ margin: 20, color: 'rgba(73, 73, 73, 0.6)'}}>
          <div style={{textAlign: 'center'}}>
            {packageInfo && packageInfo.version &&
              <p style={{textAlign: 'center', fontSize: '0.8em', fontWeight:"bold"}}>Version: {packageInfo.version}</p>
            }
            <img src={logo} width="240px" alt="IFPRS" style={{paddingBottom: 5}} />
            <div>
              <Button
                id="login"
                variant="contained"
                style={{backgroundColor: BlueAlien}}
                fullWidth
                onClick={(e) => attemptLogin(e)}
              >Login</Button>
            </div>
            <p style={{textAlign: 'left', fontSize: '0.8em', fontWeight:"bold"}}>THIS IS A NOTICE OF MONITORING OF THE DEPARTMENT OF THE INTERIOR (DOI) INFORMATION SYSTEMS.</p>
            <p style={{textAlign: 'left', fontSize: '0.8em'}}>This computer system, including all related equipment, networks, and network devices (including Internet access), is provided by the Department of the Interior (DOI) in accordance with the agency policy for official use and 
            limited personal use. All agency computer systems may be monitored for all lawful purposes, including but not limited to, ensuring that use is authorized, for management of the system, to facilitate protection against unauthorized access, 
            and to verity security procedures, survivability and operational security. 
            </p>
            <p style={{textAlign: 'left', fontSize: '0.8em'}}>Any information on this computer systems may be examined, recorded, copied and used for authorized purposes at any time. All information, including personal information, 
            placed or sent over this system may be monitored, and users of this system are reminded that such monitoring does occur. Therefore, there should be no expectation of privacy with respect to use of this system. By logging into this agency 
            computer system, you acknowledge and consent to the monitoring of this system. Evidence of your use, authorized or unauthorized, collected during monitoring may be used for civil, criminal, administrative, or other adverse action. 
            Unauthorized or illegal use may subject you to prosecution.</p>

            <p style={{textAlign: 'left', fontSize: '0.8em'}}>Data is provided by the US Department of the Interior “as is” and might contain errors or omissions. The User assumes the entire risk associated with its use of the data and bears all responsibility in determining whether the data is fit for the User’s intended use.</p><p style={{textAlign: 'left', fontSize: '0.8em'}}>The information contained in the data is dynamic and can be expected to change over time.
            </p>

            <p style={{textAlign: 'left', fontSize: '0.8em'}}>By logging in, I agree to above terms of use.</p>
            
          </div>

        <div style={{textAlign: 'center', fontSize: '0.8em'}}>
          </div>
        </div>
      </Dialog>
      </ThemeProvider>
    </div>
  )

  const finalLogic = (!userCredential) ? (loginBlock) : (isCheckingCredentials) ? (spinnerBlock) : (errorBlock)

  return (finalLogic)
}

Login.propTypes = {
  userCredential: PropTypes.any,
  isUserFound: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  isInReadWriteGroup: PropTypes.bool.isRequired,
  isCheckingCredentials: PropTypes.bool.isRequired
}

export default Login
